// client/src/routes/portal/rent.tsx — SCR-PORT-002 „Chiria mea" · PORTAL-002 phase 2.
//
// §8.16: „shows the tenant's contracts + documents + rent schedule (read-only; NO PAYMENT
// INITIATION in V1 — doc 05 §14 deferred)". All three are on this one route, because §6.6 gives
// the portal four tabs and §8.16 folds contracts and documents into this one (D-1171).
//
// „the tenant's contractS", plural, is answered by the §6.6 header selector rather than by a
// list on this page: the schedule is one contract's, and rendering two or three stacked would
// be a page a tenant with two leases has to scroll past to reach the one they came for. D-1180.
//
// The screen's own URL state — which statuses are in view, and which page of them — is
// `portalRentSearchSchema`, declared here the way the notification feed declares its page
// (§9.2.3). `contract` is on the layout route's schema and is carried, never redeclared.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { ContractScope } from '@/features/portal/ContractScope';
import { PortalSkeleton } from '@/features/portal/PortalSkeletons';
import { RentStatus } from '@/features/portal/RentStatus';
import { portalRentSearchSchema, type PortalRentSearch } from '@/features/portal/search';

export const Route = createFileRoute('/portal/rent')({
  validateSearch: portalRentSearchSchema,
  component: PortalRentScreen,
});

function PortalRentScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <>
      <PageHeader title={t('portal.rent.title')} description={t('portal.rent.subtitle')} />
      <ContractScope pending={<PortalSkeleton shape="rent" count={3} className="portal-card" />}>
        {(contract) => (
          <RentStatus
            contract={contract}
            search={search}
            onSearchChange={(next: PortalRentSearch) => {
              // The whole search is preserved and the screen's own keys replaced, so the
              // §6.6 `contract` a tenant chose survives a page turn (D-1180).
              // `resetScroll: false` so the PAGER owns the scroll. `main.tsx` creates the
              // router with `scrollRestoration: true`, which jumps the page to the top the
              // instant a search param changes — measured at 216 px to 0 between two 50 ms
              // samples — and that jump beat the pager's smooth scroll every time. The router
              // no longer moves the page on these navigations; `PortalPager` animates it
              // instead, and a filter change simply leaves the reader where they were
              // (D-1674).
              void navigate({
                search: (previous) => ({ ...previous, ...next }),
                resetScroll: false,
              });
            }}
          />
        )}
      </ContractScope>
    </>
  );
}
